// unidrogas.component.ts
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ReportService } from './../../services/report.service';

interface ColeccionEan {
  id: number;
  name: string;
  description?: string;
  total_codes?: number;
  created_at?: string;
}

@Component({
  selector: 'app-unidrogas',
  templateUrl: './unidrogas.component.html',
  styleUrls: ['./unidrogas.component.css']
})
export class UnidrogasComponent implements OnInit {
  reportForm: FormGroup;
  reportData: any[] = [];
  resumen: any = null;
  estadisticas: any = null;
  coleccionesEan: ColeccionEan[] = [];
  loading: boolean = false;
  loadingColecciones: boolean = false;
  error: string = '';

  // Opciones para el select de orden
  ordenOptions = [
    { value: '', label: 'Ninguno (Sin filtro)' },
    { value: 'desc', label: 'Más Recientes' },
    { value: 'asc', label: 'Más Antiguos' }
  ];

  constructor(
    private fb: FormBuilder,
    private reportService: ReportService
  ) {
    this.reportForm = this.fb.group({
      fecha_inicio: ['', Validators.required],
      fecha_fin: ['', Validators.required],
      incluir_detalle: [true],
      orden_fecha: [''], // Cambiado a vacío para "Ninguno" por defecto
      filtro_ean_id: [''] // Nuevo campo para el filtro EAN
    });
  }

  ngOnInit() {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    this.reportForm.patchValue({
      fecha_inicio: this.formatDate(lastMonth),
      fecha_fin: this.formatDate(today)
    });

    // Cargar las colecciones EAN al inicializar
    this.loadColeccionesEan();
  }

  private formatDate(date: Date): string {
    return date.toISOString().split('T')[0];
  }

  /**
   * Cargar las colecciones EAN disponibles
   */
  loadColeccionesEan(): void {
    this.loadingColecciones = true;

    this.reportService.listarColeccionesEan().subscribe({
      next: (response: any) => {
        // La respuesta viene en un objeto con propiedad "filtros"
        this.coleccionesEan = response.filtros || [];
        this.loadingColecciones = false;
      },
      error: (error) => {
        console.error('Error al cargar colecciones EAN:', error);
        this.coleccionesEan = [];
        this.loadingColecciones = false;
        // No mostrar error crítico, solo log
      }
    });
  }

  onSubmit(): void {
    if (this.reportForm.valid) {
      this.loading = true;
      this.error = '';

      const params = this.reportForm.value;

      // Limpiar parámetros vacíos para evitar enviar valores innecesarios
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });

      this.reportService.getUnidrogasReport(params).subscribe({
        next: (response: any) => {
          // Mapear la respuesta correctamente
          this.resumen = response.resumen || null;
          this.estadisticas = response.estadisticas_unidrogas || null;
          this.reportData = response.detalle || [];
          this.loading = false;
        },
        error: (error) => {
          console.error('Error al cargar reporte:', error);
          this.error = 'Error al cargar el reporte. Por favor, intente nuevamente.';
          this.loading = false;
        }
      });
    }
  }

  exportToExcel(): void {
    if (this.reportData.length > 0) {
      // Preparar datos para exportar con orden lógico y validación de propiedades null
      const dataForExcel = this.reportData.map(item => {
        const baseData = {
          // 1. INFORMACIÓN DE LA TRANSACCIÓN
          'Fecha': item.fecha || 'N/A',
          'Factura Despacho': item.facturadespacho || 'N/A',
          'Tipo Venta': item.tipoventa || 'N/A',

          // 2. INFORMACIÓN DEL CLIENTE
          'CC Cliente': item.CC_cliente || 'N/A',
          'Nombre Cliente': item.nombre_cliente || 'N/A',
          'Ciudad': item.ciudad || 'N/A',
          'Sucursal': item.sucursal || 'N/A',
          'Código Sucursal': item.codigosuc || 'N/A',

          // 3. INFORMACIÓN DEL MÉDICO
          'Código Médico': item.codigomedico || 'N/A',
          'Nombre Médico': item.nombremedico || 'N/A',
          'Especialidad Médico': item.espacialidad_medico || 'N/A',
          'Doctor Encontrado': item.doctor_encontrado ? 'Sí' : 'No',

          // 4. INFORMACIÓN DEL PRODUCTO
          'Código Producto': item.codigoproducto || 'N/A',
          'Nombre Comercial': item.nombrecomercial || 'N/A',
          'Cantidad': item.cantidad || 'N/A',
          'Unidad': item.unidad || 'N/A',
          'Valor': item.valor || 'N/A',

          // 5. INFORMACIÓN DEL LABORATORIO
          'Código Lab': item.codigolab || 'N/A',
          'Laboratorio': item.laboratorio || 'N/A',

          // 6. INFORMACIÓN DEL EMPLEADO/VENDEDOR
          'CC Empleado': item.cc_empleado || 'N/A',
          'Código Empleado ID': item.codigoempleado_ID || 'N/A',
          'Empleado': item.empleado || 'N/A',

          // 7. INFORMACIÓN DE PLANIFICACIÓN Y VISITADOR
          'Planificación Encontrada': item.planificacion_encontrada ? 'Sí' : 'No',
          'Visitador Asignado': item.visitador_asignado ? 'Sí' : 'No',
          'Nombre Visitador': (item.visitador_info && item.visitador_info.nombre) ? item.visitador_info.nombre : 'N/A',
          'Teléfono Visitador': (item.visitador_info && item.visitador_info.telefono) ? item.visitador_info.telefono : 'N/A'
        };

        // Agregar campos de planificación solo si existe planificación
        if (item.planificaciones && item.planificaciones.length > 0) {
          const planificacion = item.planificaciones[0];

          baseData['Fecha Inicio Planificación'] = planificacion.fecha_inicio || 'N/A';
          baseData['Actividad'] = (planificacion.actividad && planificacion.actividad.titulo) ? planificacion.actividad.titulo : 'N/A';
          baseData['Fecha Actividad Completada'] = (planificacion.actividad && planificacion.actividad.fecha_completada) ? planificacion.actividad.fecha_completada : 'N/A';
        }

        // Agregar información de fuente y filtros al final
        baseData['Fuente'] = item.fuente || 'N/A';
        baseData['Fuente Nombre'] = item.fuente_nombre || 'N/A';
        baseData['Filtro Aplicado'] = item.filtro_aplicado || 'N/A';

        return baseData;
      });

      this.reportService.exportToExcel(dataForExcel, 'reporte');
    } else {
      console.warn('No hay datos para exportar');
    }
  }

  exportResumenToExcel(): void {
    if (this.resumen) {
      const resumenData = [{
        'Total Prescripciones': this.resumen.total_prescripciones,
        'Prescripciones con Doctor': this.resumen.prescripciones_con_doctor_unidrogas,
        'Prescripciones con Planificación': this.resumen.prescripciones_con_planificacion,
        'Prescripciones con Visitador': this.resumen.prescripciones_con_visitador,
        '% Doctores': this.resumen.porcentaje_doctores_unidrogas,
        '% con Planificación': this.resumen.porcentaje_con_planificacion,
        '% con Visitador': this.resumen.porcentaje_con_visitador
      }];

      this.reportService.exportToExcel(resumenData, 'Resumen');
    }
  }

  clearResults(): void {
    this.reportData = [];
    this.resumen = null;
    this.estadisticas = null;
    this.error = '';
  }

  /**
   * Limpiar todos los filtros y resultados
   */
  clearAllFilters(): void {
    const today = new Date();
    const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);

    this.reportForm.patchValue({
      fecha_inicio: this.formatDate(lastMonth),
      fecha_fin: this.formatDate(today),
      incluir_detalle: true,
      orden_fecha: '',
      filtro_ean_id: ''
    });

    this.clearResults();
  }

  trackByFn(index: number, item: any): any {
    return item.CC_cliente + '_' + item.fecha + '_' + item.codigoproducto || index;
  }

  // Métodos auxiliares para formatear datos
  formatCurrency(value: string | number): string {
    if (value == null || value === undefined || value === '') {
      return '$0';
    }
    const num = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(num)) {
      return '$0';
    }
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP'
    }).format(num);
  }

  formatPercentage(value: number): string {
    if (value == null || value === undefined || isNaN(value)) {
      return '0.00%';
    }
    return value.toFixed(2) + '%';
  }

  // Método para calcular porcentaje de prescripciones por fuente
  getPercentageFuente(fuente: 'dropopular' | 'unidrogas'): number {
    if (!this.resumen || !this.resumen.prescripciones_por_fuente) {
      return 0;
    }

    const dropopular = this.resumen.prescripciones_por_fuente.dropopular || 0;
    const unidrogas = this.resumen.prescripciones_por_fuente.unidrogas || 0;
    const total = dropopular + unidrogas;

    if (total === 0) {
      return 0;
    }

    const valor = this.resumen.prescripciones_por_fuente[fuente] || 0;
    return Math.round((valor / total) * 100);
  }

  /**
   * Obtener el nombre de la colección EAN seleccionada
   */
  getSelectedColeccionName(): string {
    const selectedId = this.reportForm.get('filtro_ean_id').value;
    if (!selectedId) return '';

    const coleccion = this.coleccionesEan.find(c => c.id.toString() === selectedId.toString());
    return coleccion ? coleccion.name : '';
  }
}
